// GENERATED FILE. DO NOT EDIT.
//
// One method per Data API operation, generated from the Data API operation catalog the
// Mountaya platform builds from the SDL, not from the SDL itself. The catalog carries each
// operation's full-selection document as the server prints it, which is what the documents
// below are; the input and result types come from schema.gen.ts, which IS generated from
// the SDL.
//
// Each method takes the operation's input and an optional options bag. Omitting `select`
// sends the generated full-selection document below, verbatim; passing one prints a
// narrower document from the tree in selection.gen.ts and narrows the return type to
// match. `signal` and `retry` in the bag reach the transport as a REST call's would.
//
// An operation whose result holds a `DateTime` passes the transport a mask naming those
// fields, read off the SDL, so they arrive as a Date and no other string does.
//
// The SDL is authored in mountayaapp/schemas (graphql/data); edit it there, not here.

import { runOperation } from "../core/rules/graphql.js";
import {
  type DataOptions,
  type DataResult,
  documentFor,
  type Selection,
} from "../core/rules/selection.js";
import type { MountayaTransport } from "../core/rules/transport.js";
import type {
  AnalyzeGeometryInput,
  AnalyzeGeometryResult,
  DirectionsInput,
  DirectionsResult,
  ExportItineraryInput,
  ExportItineraryResult,
  GeocodeInput,
  GeocodeResult,
  IsochronesInput,
  IsochronesResult,
  ItineraryInput,
  ItineraryResult,
  MatrixInput,
  MatrixResult,
  PoisInput,
  PoisResult,
  ReverseGeocodeInput,
  ReverseGeocodeResult,
  SearchInput,
  SearchResult,
  SuggestionsInput,
  SuggestionsResult,
  TerrainGridInput,
  TerrainGridResult,
  TerrainProfileInput,
  TerrainProfileResult,
  TerrainSamplesInput,
  TerrainSamplesResult,
} from "./schema.gen.js";
import {
  analyzeGeometryFields,
  directionsFields,
  exportItineraryFields,
  geocodeFields,
  isochronesFields,
  itineraryFields,
  matrixFields,
  poisFields,
  reverseGeocodeFields,
  searchFields,
  suggestionsFields,
  terrainGridFields,
  terrainProfileFields,
  terrainSamplesFields,
} from "./selection.gen.js";

/**
 * Analyze surface and way type of an existing geometry.
 * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
 */
export const analyzeGeometry = <
  S extends Selection<typeof analyzeGeometryFields> | undefined = undefined,
>(
  transport: MountayaTransport,
  input: AnalyzeGeometryInput,
  options?: DataOptions<S>,
): Promise<DataResult<AnalyzeGeometryResult, S>> =>
  runOperation(
    transport,
    "analyzeGeometry",
    documentFor(
      "analyzeGeometry",
      "AnalyzeGeometryInput!",
      analyzeGeometryFields,
      "query AnalyzeGeometry($input: AnalyzeGeometryInput!) {\n  analyzeGeometry(input: $input) {\n    analysis {\n      elevation {\n        altitude {\n          max\n          min\n        }\n        ascent\n        descent\n        gradient {\n          max\n          min\n        }\n      }\n      elevations\n      slopeInfo {\n        fromIndex\n        toIndex\n        value\n      }\n      speedInfo {\n        fromIndex\n        toIndex\n        value\n      }\n      surfaceInfo {\n        fromIndex\n        toIndex\n        value\n      }\n      trailDifficultyInfo {\n        fromIndex\n        toIndex\n        value\n      }\n      wayTypeInfo {\n        fromIndex\n        toIndex\n        value\n      }\n      advisories {\n        tollway\n        restrictedAccess\n        tollwayInfo {\n          fromIndex\n          toIndex\n          value\n        }\n        roadAccessInfo {\n          fromIndex\n          toIndex\n          values\n        }\n      }\n    }\n    duration\n  }\n}\n",
      options?.select,
    ),
    input,
    options,
  );

/**
 * Compute directions between waypoints for a given activity.
 * Returns one or more routes with distance, duration, geometry, and analysis.
 * A waypoint that no routable way reaches for the activity is refused with VALIDATION_FAILED at ["input", "waypoints"].
 * A request the routing engine itself refuses is VALIDATION_FAILED at ["input"], and its message is the engine's own, in English whatever the Accept-Language header asks for.
 * BACKCOUNTRY_SKIING, GRAVEL_BIKE and MOUNTAIN_BIKE have no routing at all and are VALIDATION_FAILED at ["input", "activity"].
 * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
 */
export const directions = <S extends Selection<typeof directionsFields> | undefined = undefined>(
  transport: MountayaTransport,
  input: DirectionsInput,
  options?: DataOptions<S>,
): Promise<DataResult<DirectionsResult, S>> =>
  runOperation(
    transport,
    "directions",
    documentFor(
      "directions",
      "DirectionsInput!",
      directionsFields,
      "query Directions($input: DirectionsInput!) {\n  directions(input: $input) {\n    routes {\n      distance\n      duration\n      elevation {\n        altitude {\n          max\n          min\n        }\n        ascent\n        descent\n        gradient {\n          max\n          min\n        }\n      }\n      geometry\n      analysis {\n        elevation {\n          altitude {\n            max\n            min\n          }\n          ascent\n          descent\n          gradient {\n            max\n            min\n          }\n        }\n        elevations\n        slopeInfo {\n          fromIndex\n          toIndex\n          value\n        }\n        speedInfo {\n          fromIndex\n          toIndex\n          value\n        }\n        surfaceInfo {\n          fromIndex\n          toIndex\n          value\n        }\n        trailDifficultyInfo {\n          fromIndex\n          toIndex\n          value\n        }\n        wayTypeInfo {\n          fromIndex\n          toIndex\n          value\n        }\n        advisories {\n          tollway\n          restrictedAccess\n          tollwayInfo {\n            fromIndex\n            toIndex\n            value\n          }\n          roadAccessInfo {\n            fromIndex\n            toIndex\n            values\n          }\n        }\n      }\n      avoidance\n      summary\n      steps {\n        fromIndex\n        toIndex\n        type\n        name\n        distance\n        duration\n        exitNumber\n      }\n    }\n  }\n}\n",
      options?.select,
    ),
    input,
    options,
  );

/**
 * Prepare a download of an itinerary in a route file format.
 * It returns a short-lived signed URL rather than the file itself.
 * The bytes of a GPX or FIT are of no use to an assistant reading a response, and inlining them would spend the whole reply on a payload nobody reads; a URL can be handed straight to the user or fetched by a client that wants the file.
 * What may be exported is what `itinerary` serves.
 * An itinerary of your key's own organization, public or private, is exported as its current draft under a session token naming a member, provided the organization holds Map Publishing: the draft of an organization whose agreement does not include it is refused with PAYMENT_REQUIRED, and no URL is minted.
 * Under a session token naming nobody, such as the one an Embedded Studio's read-only viewer runs on, your own organization's itinerary, public or private, is exported as it was last published instead, never as its draft: one never published or taken down, or whose organization's agreement does not include Map Publishing, answers NOT_FOUND.
 * The token Mountaya's MCP server holds for its own requests is the one exception, and exports the draft.
 * Another organization's public itinerary is exported as it was last published, and one that organization has never published, or has taken down, answers NOT_FOUND, as does one whose organization's agreement does not include Map Publishing.
 * An id no itinerary carries and an itinerary your key may not read both answer NOT_FOUND.
 * The URL names one itinerary in one format and nothing else, and stops working ten minutes after it is minted.
 * Every download checks again that the itinerary is still served to the organization the URL was minted for, so a link to another organization's itinerary stops working once that itinerary is made private or taken down, or its organization's agreement stops including Map Publishing.
 * The URL also names the key that minted it, and every download checks again that this key is still active and still of that organization: revoking the key ends every URL it minted.
 * A URL minted under a session token naming a member is downloaded as that member only while they remain one: once they are removed from the organization, the download is served what a URL minted under a session token naming nobody is, the itinerary as it was last published, and 404 when it has no publication.
 * Downloads beyond 60 a minute from one address, an IPv6 address counted by its /64 prefix, answer 429, and so do downloads beyond 6,000 a minute of the URLs one organization's keys minted, counted once a link is known to be genuine and before the file is rendered.
 * An expired or altered link, or one whose key has been revoked, answers 404.
 * The refusal a mint gives for the itinerary itself is given again by the download when it has come to apply since, as it is rather than as that 404: a plain 402 PAYMENT_REQUIRED for a draft whose organization does not hold Map Publishing.
 * Nothing is stored: this creates no share and leaves no state behind.
 * A personal itinerary, made from an individual account and owned by no organization, is never exported and no URL is minted: your key reads it as published, as it reads another organization's public itinerary, and a personal itinerary is never published, so it answers NOT_FOUND, public or private, like any other itinerary your key may not read.
 * An itinerary of more than 500,000 positions, every position of its segments' geometry, the vias of its routed lines included, plus one per waypoint, is more than one answer carries: it is refused with PAYLOAD_TOO_LARGE at ["input"], naming none of its fields, and no URL is minted.
 * The positions are counted over the whole itinerary, after every other refusal, NOT_FOUND included, and the download refuses it the same way, with a 413, once the itinerary has grown past that since the URL was minted.
 * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
 */
export const exportItinerary = <
  S extends Selection<typeof exportItineraryFields> | undefined = undefined,
>(
  transport: MountayaTransport,
  input: ExportItineraryInput,
  options?: DataOptions<S>,
): Promise<DataResult<ExportItineraryResult, S>> =>
  runOperation(
    transport,
    "exportItinerary",
    documentFor(
      "exportItinerary",
      "ExportItineraryInput!",
      exportItineraryFields,
      "query ExportItinerary($input: ExportItineraryInput!) {\n  exportItinerary(input: $input) {\n    url\n    filename\n    format\n    expiresAt\n  }\n}\n",
      options?.select,
    ),
    input,
    options,
    { expiresAt: true },
  );

/**
 * Forward geocode a free-text query into a list of named places.
 * Returns cities, regions, natural features, and addresses ranked by relevance.
 * A query the geocoder itself refuses is VALIDATION_FAILED at ["input"], and its message is the geocoder's own, in English whatever the Accept-Language header asks for.
 * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
 */
export const geocode = <S extends Selection<typeof geocodeFields> | undefined = undefined>(
  transport: MountayaTransport,
  input: GeocodeInput,
  options?: DataOptions<S>,
): Promise<DataResult<GeocodeResult, S>> =>
  runOperation(
    transport,
    "geocode",
    documentFor(
      "geocode",
      "GeocodeInput!",
      geocodeFields,
      "query Geocode($input: GeocodeInput!) {\n  geocode(input: $input) {\n    count\n    places {\n      osm {\n        id\n        type\n        key\n        value\n      }\n      name\n      label\n      layer\n      category\n      point\n      bounds\n      country\n      countryName\n      region\n      county\n      city\n      postcode\n      street\n      houseNumber\n    }\n  }\n}\n",
      options?.select,
    ),
    input,
    options,
  );

/**
 * Compute isochrones (reachability polygons) from one or more locations for a given activity.
 * Returns polygons representing areas reachable within the specified range.
 * The input's own bounds (one to five locations, one to six ranges, each range at most 7,200 seconds or 25,000 meters, and a speed that stretches no time range past 7,200 seconds) are checked first, each refusal at the field it names.
 * A request within them that the routing engine still refuses, its own ceilings having moved below these, is VALIDATION_FAILED at ["input"], and its message is the engine's own, in English whatever the Accept-Language header asks for.
 * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
 */
export const isochrones = <S extends Selection<typeof isochronesFields> | undefined = undefined>(
  transport: MountayaTransport,
  input: IsochronesInput,
  options?: DataOptions<S>,
): Promise<DataResult<IsochronesResult, S>> =>
  runOperation(
    transport,
    "isochrones",
    documentFor(
      "isochrones",
      "IsochronesInput!",
      isochronesFields,
      "query Isochrones($input: IsochronesInput!) {\n  isochrones(input: $input) {\n    isochrones {\n      value\n      area\n      reachFactor\n      center\n      geometry\n    }\n  }\n}\n",
      options?.select,
    ),
    input,
    options,
  );

/**
 * Read a single itinerary by identifier, with its ordered segments and waypoints.
 * What is served depends on who owns it.
 * An itinerary of your key's own organization, public or private, is served as its current draft under a session token naming a member.
 * Under a session token naming nobody, such as the one an Embedded Studio's read-only viewer runs on, it is served as it was last published, exactly as a bare publishable key is served it on the Map Studio API, and one never published, taken down, or whose organization's agreement does not include Map Publishing answers NOT_FOUND; the token Mountaya's MCP server holds for its own requests is the one exception, and is served the draft.
 * Another organization's public itinerary ('anyone with the link') is served as it was last published, and one that organization has never published answers NOT_FOUND, as does one whose organization's agreement does not include Map Publishing; another organization's private itinerary is never served.
 * A personal itinerary, made from an individual account and owned by no organization, is readable only when it is public, and then exactly like another organization's public itinerary.
 * An id no itinerary carries and an itinerary you may not read both answer NOT_FOUND, so the answer never confirms that an id exists.
 * An itinerary of more than 500,000 positions, every position of its segments' geometry, the vias of its routed lines included, plus one per waypoint, is more than one answer carries: it is refused with PAYLOAD_TOO_LARGE at ["input"], naming none of its fields, as `exportItinerary` refuses it.
 * The positions are counted over the whole itinerary before anything is loaded, whatever the selection asks for, and the refusal comes after every other, NOT_FOUND included.
 * Within that ceiling, the geometry and the points are decoded only when the selection asks for them, which changes nothing in what is answered.
 * This is the companion to the itineraries target of `search`, which returns references only.
 * Pair it with `terrainProfile` or `analyzeGeometry` to answer questions about the ground a saved route actually covers, sending a segment's LineString positions reduced to [longitude, latitude], as `ItinerarySegment.
 * geometry` describes.
 * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
 */
export const itinerary = <S extends Selection<typeof itineraryFields> | undefined = undefined>(
  transport: MountayaTransport,
  input: ItineraryInput,
  options?: DataOptions<S>,
): Promise<DataResult<ItineraryResult, S>> =>
  runOperation(
    transport,
    "itinerary",
    documentFor(
      "itinerary",
      "ItineraryInput!",
      itineraryFields,
      "query Itinerary($input: ItineraryInput!) {\n  itinerary(input: $input) {\n    id\n    name\n    description\n    activitySlug\n    distance\n    ascent\n    isPrivate\n    organizationId\n    segments {\n      id\n      name\n      description\n      position\n      groupId\n      distance\n      duration\n      ascent\n      descent\n      altitude {\n        max\n        min\n      }\n      gradient {\n        max\n        min\n      }\n      geometry\n    }\n    waypoints {\n      id\n      name\n      description\n      position\n      point\n      amenities\n      timing {\n        cutoffAt\n        stopDuration\n      }\n      contact {\n        website\n        phone\n        email\n        address\n        openingHours\n        bookingUrl\n      }\n      media {\n        photos {\n          url\n          caption\n        }\n        links {\n          label\n          url\n        }\n      }\n      properties {\n        key\n        label\n        value\n        format\n      }\n    }\n    groups {\n      id\n      name\n      color\n      speed\n    }\n    segmentCount\n    waypointCount\n    groupCount\n  }\n}\n",
      options?.select,
    ),
    input,
    options,
    { waypoints: [{ timing: { cutoffAt: true } }] },
  );

/**
 * Compute a time-distance matrix between locations for a given activity.
 * Returns duration and/or distance grids between all source-destination pairs.
 * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
 */
export const matrix = <S extends Selection<typeof matrixFields> | undefined = undefined>(
  transport: MountayaTransport,
  input: MatrixInput,
  options?: DataOptions<S>,
): Promise<DataResult<MatrixResult, S>> =>
  runOperation(
    transport,
    "matrix",
    documentFor(
      "matrix",
      "MatrixInput!",
      matrixFields,
      "query Matrix($input: MatrixInput!) {\n  matrix(input: $input) {\n    durations\n    distances\n  }\n}\n",
      options?.select,
    ),
    input,
    options,
  );

/**
 * List points-of-interest matching one or more categories inside a bounding box or a buffered geometry.
 * Returns POIs ranked by relevance and, when a buffered area is provided, annotated with their distance to the geometry.
 * Neither area is required: with neither, the categories are looked up with no geographic filter.
 * `limit` caps what the geocoder is asked for, and a POI outside the buffered area, or a feature that maps to no POI category, is dropped after that, so fewer than `limit` POIs can come back.
 * A query the geocoder itself refuses is VALIDATION_FAILED at ["input"], and its message is the geocoder's own, in English whatever the Accept-Language header asks for.
 * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
 */
export const pois = <S extends Selection<typeof poisFields> | undefined = undefined>(
  transport: MountayaTransport,
  input: PoisInput,
  options?: DataOptions<S>,
): Promise<DataResult<PoisResult, S>> =>
  runOperation(
    transport,
    "pois",
    documentFor(
      "pois",
      "PoisInput!",
      poisFields,
      "query Pois($input: PoisInput!) {\n  pois(input: $input) {\n    count\n    pois {\n      osm {\n        id\n        type\n        key\n        value\n      }\n      name\n      category\n      point\n      elevation\n      tags {\n        key\n        value\n      }\n      distanceMeters\n      country\n      countryName\n      region\n      city\n    }\n  }\n}\n",
      options?.select,
    ),
    input,
    options,
  );

/**
 * Reverse geocode a [longitude, latitude] point into a list of named places.
 * Returns the place closest to the point, optionally constrained by layer or radius.
 * A lookup the geocoder itself refuses is VALIDATION_FAILED at ["input"], with the geocoder's own English message.
 * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
 */
export const reverseGeocode = <
  S extends Selection<typeof reverseGeocodeFields> | undefined = undefined,
>(
  transport: MountayaTransport,
  input: ReverseGeocodeInput,
  options?: DataOptions<S>,
): Promise<DataResult<ReverseGeocodeResult, S>> =>
  runOperation(
    transport,
    "reverseGeocode",
    documentFor(
      "reverseGeocode",
      "ReverseGeocodeInput!",
      reverseGeocodeFields,
      "query ReverseGeocode($input: ReverseGeocodeInput!) {\n  reverseGeocode(input: $input) {\n    point\n    places {\n      osm {\n        id\n        type\n        key\n        value\n      }\n      name\n      label\n      layer\n      category\n      point\n      bounds\n      country\n      countryName\n      region\n      county\n      city\n      postcode\n      street\n      houseNumber\n    }\n    count\n  }\n}\n",
      options?.select,
    ),
    input,
    options,
  );

/**
 * Unified full-text search across geocoded places, POIs, ways, and Mountaya entities (itineraries, collections, zones).
 * Each enabled target runs on its own: a failed target leaves its section null and adds an entry to `errors`.
 * A malformed `focus` or `bbox` fails the request, as does one whose every enabled target failed, which counts toward neither the monthly allowance nor an invoice (see `SearchResult`).
 * A feature found by more than one of places, pois and ways is returned once, in `ways` first, then `pois`.
 * The entity targets list your key's own organization's entities only, never another organization's or a personal one.
 * A session token naming a member lists them public and private, an itinerary or a zone as its draft.
 * A session token naming nobody lists what a bare publishable key lists: public itineraries and zones currently published, each as last published, and public collections.
 * The token Mountaya's MCP server holds lists as a member's does.
 * Requires a publishable key with the DATA scope, or with the MCP scope through Mountaya's MCP server, and a session token.
 */
export const search = <S extends Selection<typeof searchFields> | undefined = undefined>(
  transport: MountayaTransport,
  input: SearchInput,
  options?: DataOptions<S>,
): Promise<DataResult<SearchResult, S>> =>
  runOperation(
    transport,
    "search",
    documentFor(
      "search",
      "SearchInput!",
      searchFields,
      "query Search($input: SearchInput!) {\n  search(input: $input) {\n    query\n    language\n    targets {\n      places\n      pois\n      ways\n      itineraries\n      collections\n      zones\n    }\n    places {\n      count\n      places {\n        osm {\n          id\n          type\n          key\n          value\n        }\n        name\n        label\n        layer\n        category\n        point\n        bounds\n        country\n        countryName\n        region\n        county\n        city\n        postcode\n        street\n        houseNumber\n      }\n    }\n    pois {\n      count\n      pois {\n        osm {\n          id\n          type\n          key\n          value\n        }\n        name\n        category\n        point\n        elevation\n        tags {\n          key\n          value\n        }\n        distanceMeters\n        country\n        countryName\n        region\n        city\n      }\n    }\n    ways {\n      count\n      ways {\n        osm {\n          id\n          type\n          key\n          value\n        }\n        name\n        highway\n        surface\n        sacScale\n        point\n        bounds\n        coordinates\n        activityCompatible\n        country\n        countryName\n        region\n        city\n      }\n      activityApplied\n    }\n    itineraries {\n      count\n      itineraries {\n        id\n        name\n        description\n        distance\n        ascent\n        activitySlug\n        start\n        bounds\n        isPrivate\n        organizationId\n        score\n        matchedField\n      }\n    }\n    collections {\n      count\n      collections {\n        id\n        name\n        description\n        count {\n          itineraries\n          zones\n        }\n        bounds\n        isPrivate\n        organizationId\n        score\n        matchedField\n      }\n    }\n    zones {\n      count\n      zones {\n        id\n        name\n        description\n        level\n        mode\n        point\n        bounds\n        isPrivate\n        organizationId\n        score\n        matchedField\n      }\n    }\n    errors {\n      target\n      code\n      message\n    }\n  }\n}\n",
      options?.select,
    ),
    input,
    options,
  );

/**
 * Generate route suggestions matching distance constraints for a given activity.
 * Returns multiple route candidates with elevation data, allowing the client to select based on elevation gain, distance, or shape preference.
 * Suggested routes NEVER cross a tollway or an access-restricted way.
 * There is no opt-out: proposing somewhere to go is a different act from a user deliberately routing across such a way, which `directions` still allows.
 * Every candidate's `analysis.
 * advisories` therefore reports no hazard.
 * When no hazard-free candidate can be generated from the given points, this fails with VALIDATION_FAILED at ["input", "start"] rather than returning one that crosses something, and so does a request for which no candidate can be routed at all.
 * BACKCOUNTRY_SKIING, GRAVEL_BIKE and MOUNTAIN_BIKE have no routing and are VALIDATION_FAILED at ["input", "activity"].
 * For SKI_TOURING, CROSS_COUNTRY_SKIING and SNOWSHOE_WALKING the hazard check reads the routing engine's route-level warning, as there is no per-stretch data for those three (see RouteAdvisories).
 * That warning is raised by any access tag, so a candidate crossing a PERMISSIVE, AGRICULTURAL or FORESTRY way is dropped as well.
 * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
 */
export const suggestions = <S extends Selection<typeof suggestionsFields> | undefined = undefined>(
  transport: MountayaTransport,
  input: SuggestionsInput,
  options?: DataOptions<S>,
): Promise<DataResult<SuggestionsResult, S>> =>
  runOperation(
    transport,
    "suggestions",
    documentFor(
      "suggestions",
      "SuggestionsInput!",
      suggestionsFields,
      "query Suggestions($input: SuggestionsInput!) {\n  suggestions(input: $input) {\n    routes {\n      distance\n      duration\n      elevation {\n        altitude {\n          max\n          min\n        }\n        ascent\n        descent\n        gradient {\n          max\n          min\n        }\n      }\n      geometry\n      analysis {\n        elevation {\n          altitude {\n            max\n            min\n          }\n          ascent\n          descent\n          gradient {\n            max\n            min\n          }\n        }\n        elevations\n        slopeInfo {\n          fromIndex\n          toIndex\n          value\n        }\n        speedInfo {\n          fromIndex\n          toIndex\n          value\n        }\n        surfaceInfo {\n          fromIndex\n          toIndex\n          value\n        }\n        trailDifficultyInfo {\n          fromIndex\n          toIndex\n          value\n        }\n        wayTypeInfo {\n          fromIndex\n          toIndex\n          value\n        }\n        advisories {\n          tollway\n          restrictedAccess\n          tollwayInfo {\n            fromIndex\n            toIndex\n            value\n          }\n          roadAccessInfo {\n            fromIndex\n            toIndex\n            values\n          }\n        }\n      }\n      summary\n      steps {\n        fromIndex\n        toIndex\n        type\n        name\n        distance\n        duration\n        exitNumber\n      }\n      seed\n    }\n  }\n}\n",
      options?.select,
    ),
    input,
    options,
  );

/**
 * Sample the surface over a rectangular grid inside a bounding box, for area reconnaissance: finding high ground, a steep band, or a slope facing a given direction before routing through it.
 * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
 */
export const terrainGrid = <S extends Selection<typeof terrainGridFields> | undefined = undefined>(
  transport: MountayaTransport,
  input: TerrainGridInput,
  options?: DataOptions<S>,
): Promise<DataResult<TerrainGridResult, S>> =>
  runOperation(
    transport,
    "terrainGrid",
    documentFor(
      "terrainGrid",
      "TerrainGridInput!",
      terrainGridFields,
      "query TerrainGrid($input: TerrainGridInput!) {\n  terrainGrid(input: $input) {\n    count\n    coveredCount\n    zoom\n    resolutionMeters\n    cols\n    rows\n    cells {\n      point\n      elevation\n      slope\n      aspect\n      aspectOctant\n      steepnessBand\n      distanceMeters\n    }\n  }\n}\n",
      options?.select,
    ),
    input,
    options,
  );

/**
 * Sample the surface along a geometry at a fixed interval, producing an elevation profile as data.
 * Use it to answer how steep a route is and where the hard sections sit.
 * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
 */
export const terrainProfile = <
  S extends Selection<typeof terrainProfileFields> | undefined = undefined,
>(
  transport: MountayaTransport,
  input: TerrainProfileInput,
  options?: DataOptions<S>,
): Promise<DataResult<TerrainProfileResult, S>> =>
  runOperation(
    transport,
    "terrainProfile",
    documentFor(
      "terrainProfile",
      "TerrainProfileInput!",
      terrainProfileFields,
      "query TerrainProfile($input: TerrainProfileInput!) {\n  terrainProfile(input: $input) {\n    count\n    coveredCount\n    zoom\n    resolutionMeters\n    samples {\n      point\n      elevation\n      slope\n      aspect\n      aspectOctant\n      steepnessBand\n      distanceMeters\n    }\n  }\n}\n",
      options?.select,
    ),
    input,
    options,
  );

/**
 * Sample elevation, slope, and aspect at standalone [longitude, latitude] points.
 * Use it for readings at known coordinates; for an existing line use terrainProfile, and to search an area for features you have not located use terrainGrid.
 * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
 */
export const terrainSamples = <
  S extends Selection<typeof terrainSamplesFields> | undefined = undefined,
>(
  transport: MountayaTransport,
  input: TerrainSamplesInput,
  options?: DataOptions<S>,
): Promise<DataResult<TerrainSamplesResult, S>> =>
  runOperation(
    transport,
    "terrainSamples",
    documentFor(
      "terrainSamples",
      "TerrainSamplesInput!",
      terrainSamplesFields,
      "query TerrainSamples($input: TerrainSamplesInput!) {\n  terrainSamples(input: $input) {\n    count\n    coveredCount\n    zoom\n    resolutionMeters\n    samples {\n      point\n      elevation\n      slope\n      aspect\n      aspectOctant\n      steepnessBand\n      distanceMeters\n    }\n  }\n}\n",
      options?.select,
    ),
    input,
    options,
  );

/**
 * bind returns every Data API operation with the transport applied.
 */
export const bind = (transport: MountayaTransport) => ({
  /**
   * Analyze surface and way type of an existing geometry.
   * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
   * @see {@link analyzeGeometry}
   */
  analyzeGeometry: <S extends Selection<typeof analyzeGeometryFields> | undefined = undefined>(
    input: AnalyzeGeometryInput,
    options?: DataOptions<S>,
  ) => analyzeGeometry(transport, input, options),
  /**
   * Compute directions between waypoints for a given activity.
   * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
   * @see {@link directions}
   */
  directions: <S extends Selection<typeof directionsFields> | undefined = undefined>(
    input: DirectionsInput,
    options?: DataOptions<S>,
  ) => directions(transport, input, options),
  /**
   * Prepare a download of an itinerary in a route file format.
   * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
   * @see {@link exportItinerary}
   */
  exportItinerary: <S extends Selection<typeof exportItineraryFields> | undefined = undefined>(
    input: ExportItineraryInput,
    options?: DataOptions<S>,
  ) => exportItinerary(transport, input, options),
  /**
   * Forward geocode a free-text query into a list of named places.
   * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
   * @see {@link geocode}
   */
  geocode: <S extends Selection<typeof geocodeFields> | undefined = undefined>(
    input: GeocodeInput,
    options?: DataOptions<S>,
  ) => geocode(transport, input, options),
  /**
   * Compute isochrones (reachability polygons) from one or more locations for a given activity.
   * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
   * @see {@link isochrones}
   */
  isochrones: <S extends Selection<typeof isochronesFields> | undefined = undefined>(
    input: IsochronesInput,
    options?: DataOptions<S>,
  ) => isochrones(transport, input, options),
  /**
   * Read a single itinerary by identifier, with its ordered segments and waypoints.
   * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
   * @see {@link itinerary}
   */
  itinerary: <S extends Selection<typeof itineraryFields> | undefined = undefined>(
    input: ItineraryInput,
    options?: DataOptions<S>,
  ) => itinerary(transport, input, options),
  /**
   * Compute a time-distance matrix between locations for a given activity.
   * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
   * @see {@link matrix}
   */
  matrix: <S extends Selection<typeof matrixFields> | undefined = undefined>(
    input: MatrixInput,
    options?: DataOptions<S>,
  ) => matrix(transport, input, options),
  /**
   * List points-of-interest matching one or more categories inside a bounding box or a buffered geometry.
   * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
   * @see {@link pois}
   */
  pois: <S extends Selection<typeof poisFields> | undefined = undefined>(
    input: PoisInput,
    options?: DataOptions<S>,
  ) => pois(transport, input, options),
  /**
   * Reverse geocode a [longitude, latitude] point into a list of named places.
   * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
   * @see {@link reverseGeocode}
   */
  reverseGeocode: <S extends Selection<typeof reverseGeocodeFields> | undefined = undefined>(
    input: ReverseGeocodeInput,
    options?: DataOptions<S>,
  ) => reverseGeocode(transport, input, options),
  /**
   * Unified full-text search across geocoded places, POIs, ways, and Mountaya entities (itineraries, collections, zones).
   * Requires a publishable key with the DATA scope, or with the MCP scope through Mountaya's MCP server, and a session token.
   * @see {@link search}
   */
  search: <S extends Selection<typeof searchFields> | undefined = undefined>(
    input: SearchInput,
    options?: DataOptions<S>,
  ) => search(transport, input, options),
  /**
   * Generate route suggestions matching distance constraints for a given activity.
   * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
   * @see {@link suggestions}
   */
  suggestions: <S extends Selection<typeof suggestionsFields> | undefined = undefined>(
    input: SuggestionsInput,
    options?: DataOptions<S>,
  ) => suggestions(transport, input, options),
  /**
   * Sample the surface over a rectangular grid inside a bounding box, for area reconnaissance: finding high ground, a steep band, or a slope facing a given direction before routing through it.
   * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
   * @see {@link terrainGrid}
   */
  terrainGrid: <S extends Selection<typeof terrainGridFields> | undefined = undefined>(
    input: TerrainGridInput,
    options?: DataOptions<S>,
  ) => terrainGrid(transport, input, options),
  /**
   * Sample the surface along a geometry at a fixed interval, producing an elevation profile as data.
   * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
   * @see {@link terrainProfile}
   */
  terrainProfile: <S extends Selection<typeof terrainProfileFields> | undefined = undefined>(
    input: TerrainProfileInput,
    options?: DataOptions<S>,
  ) => terrainProfile(transport, input, options),
  /**
   * Sample elevation, slope, and aspect at standalone [longitude, latitude] points.
   * Requires a publishable key with the DATA scope, or with the MCP scope when the call arrives through Mountaya's MCP server, and a session token.
   * @see {@link terrainSamples}
   */
  terrainSamples: <S extends Selection<typeof terrainSamplesFields> | undefined = undefined>(
    input: TerrainSamplesInput,
    options?: DataOptions<S>,
  ) => terrainSamples(transport, input, options),
});
